import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { envConfig } from "~/lib/env-validation";
import { useAuth } from "~/lib/hooks/use-auth";

export type SubscriptionPlan = "FREE" | "PREMIUM" | "PRO" | "ENTERPRISE";
export type SubscriptionStatus =
	| "ACTIVE"
	| "PAST_DUE"
	| "CANCELED"
	| "TRIALING"
	| "INCOMPLETE"
	| "INCOMPLETE_EXPIRED";

/**
 * Named features mapped to their minimum required plan.
 * Must stay in sync with FEATURE_PLAN_MAP in firmindex-api/src/middleware/subscription.ts.
 */
export type PremiumFeature =
	| "score-detail"
	| "financials-export"
	| "financials-ratios"
	| "financials-detail"
	| "industry-compare"
	| "api-access"
	| "bulk-export";

const FEATURE_PLAN_MAP: Record<PremiumFeature, SubscriptionPlan> = {
	"score-detail": "PREMIUM",
	"financials-export": "PREMIUM",
	"financials-ratios": "PREMIUM",
	"financials-detail": "PREMIUM",
	"industry-compare": "PRO",
	"api-access": "PREMIUM",
	"bulk-export": "PRO",
};

interface Subscription {
	id: string;
	userId: string;
	plan: SubscriptionPlan;
	status: SubscriptionStatus;
	billingInterval: "MONTHLY" | "ANNUAL" | null;
	currentPeriodStart: string | null;
	currentPeriodEnd: string | null;
	cancelAtPeriodEnd: boolean;
	trialEnd: string | null;
	trialExtendedAt: string | null;
	createdAt: string;
	updatedAt: string;
}

const PLAN_HIERARCHY: Record<SubscriptionPlan, number> = {
	FREE: 0,
	PREMIUM: 1,
	PRO: 2,
	ENTERPRISE: 3,
};

export function useSubscription() {
	const { session } = useAuth();
	const [subscription, setSubscription] = useState<Subscription | null>(null);
	const [loading, setLoading] = useState(true);
	const [checkoutPending, setCheckoutPending] = useState(false);
	const [portalPending, setPortalPending] = useState(false);

	useEffect(() => {
		if (!session?.access_token) {
			setLoading(false);
			return;
		}

		fetch(`${envConfig.apiBaseUrl}/billing/subscription`, {
			headers: { Authorization: `Bearer ${session.access_token}` },
		})
			.then((res) => res.json())
			.then((data) => {
				if (data.id) setSubscription(data);
			})
			.catch(() => {})
			.finally(() => setLoading(false));
	}, [session?.access_token]);

	const isTrialing = subscription?.status === "TRIALING";

	const trialDaysRemaining = useMemo(() => {
		if (!subscription?.trialEnd) return 0;
		const diff = new Date(subscription.trialEnd).getTime() - Date.now();
		return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
	}, [subscription?.trialEnd]);

	const effectivePlan: SubscriptionPlan = useMemo(() => {
		if (!subscription) return "FREE";
		// Allow-list, mirroring the API's subscriptionGrantsPaidAccess: only an
		// ACTIVE subscription or a still-running trial grants the plan. Every other
		// status (PAST_DUE, CANCELED, INCOMPLETE, INCOMPLETE_EXPIRED, expired trial)
		// is FREE — otherwise the UI would offer paid features the API now rejects.
		if (subscription.status === "ACTIVE") return subscription.plan;
		if (
			subscription.status === "TRIALING" &&
			(!subscription.trialEnd || new Date(subscription.trialEnd) > new Date())
		)
			return subscription.plan;
		return "FREE";
	}, [subscription]);

	const isPremiumOrAbove = PLAN_HIERARCHY[effectivePlan] >= PLAN_HIERARCHY.PREMIUM;
	const isProOrAbove = PLAN_HIERARCHY[effectivePlan] >= PLAN_HIERARCHY.PRO;

	const canAccess = useCallback(
		(feature: PremiumFeature): boolean => {
			const requiredPlan = FEATURE_PLAN_MAP[feature];
			return PLAN_HIERARCHY[effectivePlan] >= PLAN_HIERARCHY[requiredPlan];
		},
		[effectivePlan],
	);

	const createCheckout = useCallback(
		async (plan: "PREMIUM" | "PRO", interval: "MONTHLY" | "ANNUAL") => {
			if (!session?.access_token) return;
			setCheckoutPending(true);
			let redirecting = false;

			try {
				const res = await fetch(`${envConfig.apiBaseUrl}/billing/checkout`, {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${session.access_token}`,
					},
					body: JSON.stringify({
						plan,
						interval,
						successUrl: `${window.location.origin}/cenik?billing=success`,
						cancelUrl: `${window.location.origin}/cenik`,
					}),
				});

				const data = await res.json();
				if (res.ok && data.url) {
					redirecting = true;
					window.location.href = data.url;
					return;
				}
			} catch {
				// fall through to the toast below
			} finally {
				if (!redirecting) setCheckoutPending(false);
			}
			toast.error("Platbu se nepodařilo zahájit. Zkuste to prosím později.");
		},
		[session?.access_token],
	);

	const extendTrial = useCallback(async () => {
		if (!session?.access_token) return { ok: false, error: "not-authenticated" } as const;

		try {
			const res = await fetch(`${envConfig.apiBaseUrl}/billing/extend-trial`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${session.access_token}`,
				},
			});

			if (!res.ok) {
				const data = await res.json().catch(() => ({}));
				return { ok: false, error: data.error ?? "request-failed" } as const;
			}

			const data = (await res.json()) as Subscription;
			setSubscription(data);
			return { ok: true, subscription: data } as const;
		} catch {
			return { ok: false, error: "network-error" } as const;
		}
	}, [session?.access_token]);

	const trialAlreadyExtended = subscription?.trialExtendedAt != null;

	const openPortal = useCallback(async () => {
		if (!session?.access_token) return;
		setPortalPending(true);
		let redirecting = false;

		try {
			const res = await fetch(`${envConfig.apiBaseUrl}/billing/portal`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${session.access_token}`,
				},
				body: JSON.stringify({
					returnUrl: window.location.href,
				}),
			});

			const data = await res.json();
			if (res.ok && data.url) {
				redirecting = true;
				window.location.href = data.url;
				return;
			}
		} catch {
			// fall through to the toast below
		} finally {
			if (!redirecting) setPortalPending(false);
		}
		toast.error("Správu předplatného se nepodařilo otevřít. Zkuste to prosím později.");
	}, [session?.access_token]);

	return {
		subscription,
		loading,
		isTrialing,
		trialDaysRemaining,
		trialAlreadyExtended,
		effectivePlan,
		isPremiumOrAbove,
		isProOrAbove,
		canAccess,
		checkoutPending,
		createCheckout,
		extendTrial,
		openPortal,
		portalPending,
	};
}
